import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  Box,
  Typography,
  Paper,
  Grid2,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Badge,
  Grid22,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  AttachFile as AttachIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "context/auth/auth";
import { useLayout } from "context/layout/layout";
import axios from "axios";
import config from "../../../services/config";
import Sidebar from "components/Sidebar/Sidebar";

import { AppBar, Toolbar, Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { style } from "d3";
import { templates } from "../templates";

import { colors } from "../../../utils/theme";

// Datos por defecto para nueva campaña
const defaultCampaign = {
  title: "",
  content: {
    type: "html",
    body: "",
    imgUrl: "",
  },
  enabled: false,
  sendType: "scheduled",
  targetType: "group",
  recipients: [],
  channelType: "email",
  subject: "",
  configVariables: {},
  attachments: [],
  schedule: {
    type: "monthly",
    monthly: {
      dayOfMonth: 5,
      time: "09:00",
    },
  },
  status: "draft",
};

// Opciones de configuración
const sendTypeOptions = [
  { value: "scheduled", label: "Programado", icon: <ScheduleIcon /> },
  { value: "on_demand", label: "Inmediato", icon: <SendIcon /> },
];

const targetTypeOptions = [
  { value: "group", label: "Grupo", icon: <GroupIcon /> },
  { value: "individual", label: "Individual", icon: <EmailIcon /> },
];

const channelTypeOptions = [
  { value: "email", label: "Email", icon: <EmailIcon /> },
  { value: "sms", label: "SMS", icon: <EmailIcon /> },
];

const statusOptions = [
  { value: "draft", label: "Borrador", color: "#6b7280" },
  { value: "scheduled", label: "Programado", color: "#f59e0b" },
  { value: "sent", label: "Enviado", color: "#10b981" },
  { value: "cancelled", label: "Cancelado", color: "#ef4444" },
];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`campaign-tabpanel-${index}`}
      aria-labelledby={`campaign-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const CampaignDetails = () => {
  return (
    <>
      <Sidebar />
      <CampaignEditor />
    </>
  );
};

function CampaignEditor() {
  const { _id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const layout = useLayout();
  const isEditing = Boolean(_id) && _id !== "new";

  // Estados principales
  const [campaign, setCampaign] = useState(defaultCampaign);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [newVariable, setNewVariable] = useState({ key: "", value: "" });

  const fileInputRef = useRef();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const newAttachments = [...campaign.attachments];
    newAttachments.push({ filename: file.name, file, mimeType: file.type });
    setCampaign((prev) => ({ ...prev, attachments: newAttachments }));
  };

  // Cargar campaña existente si estamos editando
  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetch(`${config.url}/marketing/${_id}`, {
        method: "GET",
        headers: {
          Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log(data);
          setCampaign(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          layout.errorAlert({ message: "Error al cargar la campaña" });
          setLoading(false);
        });
    }
  }, [_id, isEditing, layout]);

  // Handlers
  const handleSave = async () => {
    setSaving(true);

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `${config.url}/marketing/${_id}`
      : `${config.url}/marketing/create`; // ✅ Usar ruta /create para POST

    // ¿Hay archivos nuevos?
    const hasFiles = campaign.attachments.some((att) => !!att.file);

    let options = { method, headers: {}, body: null };

    if (hasFiles) {
      // ➤ Usar FormData si hay archivos
      const formData = new FormData();

      // Append campos principales (excepto attachments)
      Object.entries(campaign).forEach(([key, value]) => {
        if (key === "attachments") return; // lo procesamos por separado
        formData.append(
          key,
          typeof value === "object" ? JSON.stringify(value) : value
        );
      });

      // Procesar attachments con mejor lógica
      let fileIndex = 0; // Contador para archivos nuevos
      let infoIndex = 0; // Contador para metadata

      campaign.attachments.forEach((att) => {
        if (att.file) {
          // Archivo nuevo: agregar el file y su metadata
          formData.append("attachments", att.file);
          formData.append(
            `attachmentsInfo[${fileIndex}][filename]`,
            att.filename
          );
          formData.append(
            `attachmentsInfo[${fileIndex}][mimeType]`,
            att.mimeType
          );
          fileIndex++;
        } else if (att.url) {
          // Archivo existente: solo metadata
          formData.append(`attachmentsInfo[${infoIndex}][url]`, att.url);
          formData.append(
            `attachmentsInfo[${infoIndex}][filename]`,
            att.filename
          );
          formData.append(
            `attachmentsInfo[${infoIndex}][mimeType]`,
            att.mimeType
          );
          infoIndex++;
        }
      });

      // Debug: mostrar contenido del FormData
      console.log("📤 FormData entries:");
      for (let [key, value] of formData.entries()) {
        console.log(
          key,
          typeof value === "object" ? value.name || "[File]" : value
        );
      }

      options.body = formData;
      // No ponemos Content-Type: el navegador lo añade con boundary
      options.headers.Authorization = `Basic ${process.env.REACT_APP_BASIC_AUTH}`;
    } else {
      // ➤ Usar JSON si no hay archivos
      options.headers = {
        "Content-Type": "application/json",
        Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}`,
      };
      options.body = JSON.stringify(campaign);
    }

    try {
      console.log("🚀 Enviando request:", { method, url, hasFiles });

      const response = await fetch(url, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Error response:", errorText);
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log("✅ Respuesta exitosa:", result);

      layout.successAlert({
        message: isEditing
          ? "Campaña actualizada exitosamente"
          : "Campaña creada exitosamente",
      });
      navigate("/admin/campaigns");
    } catch (error) {
      console.error("❌ Error al guardar:", error);
      layout.errorAlert({
        message: `Error al guardar la campaña: ${error.message}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = () => {
    if (newRecipient.trim()) {
      const recipient = {
        type: campaign.channelType,
        value: newRecipient.trim(),
        _id: Date.now().toString(),
      };
      setCampaign((prev) => ({
        ...prev,
        recipients: [...prev.recipients, recipient],
      }));
      setNewRecipient("");
    }
  };

  const handleRemoveRecipient = (index) => {
    setCampaign((prev) => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index),
    }));
  };

  const handleAddVariable = () => {
    if (newVariable.key.trim() && newVariable.value.trim()) {
      setCampaign((prev) => ({
        ...prev,
        configVariables: {
          ...prev.configVariables,
          [newVariable.key]: newVariable.value,
        },
      }));
      setNewVariable({ key: "", value: "" });
    }
  };

  const handleRemoveVariable = (key) => {
    setCampaign((prev) => {
      const newVariables = { ...prev.configVariables };
      delete newVariables[key];
      return {
        ...prev,
        configVariables: newVariables,
      };
    });
  };

  const handleRemoveAttachment = (index) => {
    setCampaign((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const renderStatusBadge = (status) => {
    const statusInfo = statusOptions.find((s) => s.value === status);
    return (
      <Badge
        sx={{
          "& .MuiBadge-badge": {
            backgroundColor: statusInfo?.color || "#6b7280",
            color: "white",
            fontSize: "0.75rem",
            height: "20px",
            minWidth: "20px",
          },
        }}
        badgeContent={statusInfo?.label}
      >
        <Box sx={{ width: 8 }} />
      </Badge>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "rgb(17, 24, 39)",
        }}
      >
        <Typography variant="h6" sx={{ color: "white" }}>
          Cargando campaña...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: colors.sapphire,
        px: 0,
        py: 0,
        marginLeft: "210px",
        width: "calc(100vw - 210px)",
      }}
    >
      {/* Header */}
      <CampaignNavbar
        renderStatusBadge={renderStatusBadge}
        isEditing={isEditing}
        campaign={campaign}
        _id={_id}
        saving={saving}
        setPreviewOpen={setPreviewOpen}
        handleSave={handleSave}
      />

      <Paper
        sx={{
          backgroundColor: "rgb(31, 41, 55)",
          borderRadius: 0,
          marginTop: 4,
          minHeight: "100vh",
          width: "100%",
          justifyContent: "center",

          display: "flex",
        }}
      >
        {/* Tab General */}
        <TabPanel
          value={activeTab}
          index={0}
          fullWidth
          style={{ minWidth: "100%", height: "100%", padding: 0 }}
        >
          <Grid2 container spacing={3} sx={{ px: 3 }}>
            {/* Columna izquierda: Inputs (50%) */}
            <Grid2
              item
              xs={12}
              md={6}
              container
              direction="column"
              style={{ minWidth: "47%", margin: "20px auto" }}
            >
              {/* Título de la campaña */}
              <Grid2 item>
                <TextField
                  fullWidth
                  label="Título de la campaña"
                  value={campaign.title}
                  onChange={(e) =>
                    setCampaign((prev) => ({ ...prev, title: e.target.value }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: colors.oxfordBlue },
                      "&:hover fieldset": { borderColor: colors.oxfordBlue },
                      "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                    },
                    "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                    "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                  }}
                />
              </Grid2>

              {/* Asunto del email */}
              <Grid2 item>
                <TextField
                  fullWidth
                  label="Asunto del email"
                  value={campaign.subject}
                  onChange={(e) =>
                    setCampaign((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: colors.oxfordBlue },
                      "&:hover fieldset": { borderColor: colors.oxfordBlue },
                      "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                    },
                    "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                    "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                  }}
                />
              </Grid2>

              {/* Tipo de envío */}
              <Grid2 item>
                <Autocomplete
                  options={sendTypeOptions}
                  value={sendTypeOptions.find(
                    (opt) => opt.value === campaign.sendType
                  )}
                  onChange={(e, value) =>
                    setCampaign((prev) => ({
                      ...prev,
                      sendType: value?.value || "scheduled",
                    }))
                  }
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", gap: 1 }}
                    >
                      {option.icon}
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de envío"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#fff",
                          "& fieldset": { borderColor: colors.oxfordBlue },
                          "&:hover fieldset": { borderColor: colors.oxfordBlue },
                          "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                        },
                        "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                        "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                      }}
                    />
                  )}
                />
              </Grid2>

              {/* Fecha y hora (si no es inmediato) */}
              {campaign.sendType !== "on_demand" && (
                <Grid2 item>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Fecha y hora de envío"
                    value={
                      campaign.date
                        ? new Date(campaign.date).toISOString().slice(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      setCampaign((prev) => ({
                        ...prev,
                        date: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : "",
                      }))
                    }
                    disabled={campaign.sendType === "on_demand"}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        "& fieldset": { borderColor: colors.oxfordBlue },
                        "&:hover fieldset": { borderColor: colors.oxfordBlue },
                        "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                      },
                      "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                      "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                    }}
                  />
                </Grid2>
              )}

              {/* Switch de activación */}
              <Grid2 item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={campaign.enabled}
                      onChange={(e) =>
                        setCampaign((prev) => ({
                          ...prev,
                          enabled: e.target.checked,
                        }))
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#10b981",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#10b981",
                          },
                      }}
                    />
                  }
                  label="Campaña activa"
                  sx={{ color: "white" }}
                />
              </Grid2>

              {/* Variables de personalización */}
              {/* <Grid2 item>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  Variables de personalización
                </Typography>
                <Card sx={{ backgroundColor: "#fff", mb: 2 }}>
                  <CardContent>
                    <Grid2 container spacing={2} alignItems="center">
                      <Grid2 item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nombre de variable"
                          value={newVariable.key}
                          onChange={(e) =>
                            setNewVariable((prev) => ({
                              ...prev,
                              key: e.target.value,
                            }))
                          }
                          placeholder="ej: nombre"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#4b5563",
                              "& fieldset": { borderColor: "#6b7280" },
                              "&:hover fieldset": { borderColor: colors.oxfordBlue },
                              "&.Mui-focused fieldset": {
                                borderColor: "#10b981",
                              },
                            },
                            "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                            "& .MuiOutlinedInput-input": { color: "white" },
                          }}
                        />
                      </Grid2>
                      <Grid2 item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Valor por defecto"
                          value={newVariable.value}
                          onChange={(e) =>
                            setNewVariable((prev) => ({
                              ...prev,
                              value: e.target.value,
                            }))
                          }
                          placeholder="ej: Cliente"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#4b5563",
                              "& fieldset": { borderColor: "#6b7280" },
                              "&:hover fieldset": { borderColor: colors.oxfordBlue },
                              "&.Mui-focused fieldset": {
                                borderColor: "#10b981",
                              },
                            },
                            "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                            "& .MuiOutlinedInput-input": { color: "white" },
                          }}
                        />
                      </Grid2>
                      <Grid2 item xs={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddVariable}
                          sx={{
                            backgroundColor: "#10b981",
                            "&:hover": { backgroundColor: "#059669" },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </Grid2>
                    </Grid2>
                  </CardContent>
                </Card>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(campaign.configVariables).map(
                    ([key, value]) => (
                      <Chip
                        key={key}
                        label={`{{${key}}}: ${value}`}
                        onDelete={() => handleRemoveVariable(key)}
                        sx={{
                          backgroundColor: "#4b5563",
                          color: "white",
                          "& .MuiChip-deleteIcon": { color: colors.oxfordBlue },
                        }}
                      />
                    )
                  )}
                </Box>
              </Grid2> */}

              {/* <Grid2 item xs={10}>
                <TextField
                  fullWidth
                  label={`${
                    campaign.channelType === "email" ? "Email" : "Teléfono"
                  } del destinatario`}
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  placeholder={
                    campaign.channelType === "email"
                      ? "usuario@ejemplo.com"
                      : "+56912345678"
                  }
                  onKeyPress={(e) => e.key === "Enter" && handleAddRecipient()}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#4b5563",
                      "& fieldset": { borderColor: "#6b7280" },
                      "&:hover fieldset": { borderColor: colors.oxfordBlue },
                      "&.Mui-focused fieldset": { borderColor: "#10b981" },
                    },
                    "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                    "& .MuiOutlinedInput-input": { color: "white" },
                  }}
                />
              </Grid2> */}
              {/* <Grid2 item xs={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddRecipient}
                  sx={{
                    backgroundColor: "#10b981",
                    "&:hover": { backgroundColor: "#059669" },
                  }}
                >
                  <AddIcon />
                </Button>
              </Grid2> */}

              {campaign.attachments.length === 0 ? (
                <Box
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    const newAttachments = files.map((file) => ({
                      filename: file.name,
                      mimeType: file.type,
                      file,
                    }));
                    setCampaign((prev) => ({
                      ...prev,
                      attachments: [...prev.attachments, ...newAttachments],
                    }));
                  }}
                  onClick={() =>
                    document.getElementById("file-upload-input")?.click()
                  }
                  sx={{
                    textAlign: "center",
                    py: 6,
                    border: "2px dashed #6b7280",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#10b981" },
                  }}
                >
                  <UploadIcon sx={{ fontSize: 64, color: "#6b7280", mb: 2 }} />
                  <Typography variant="body1" sx={{ color: colors.oxfordBlue }}>
                    Arrastra archivos aquí o haz clic para subir
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Los adjuntos aparecerán aquí una vez que los agregues
                  </Typography>
                  <input
                    id="file-upload-input"
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const newAttachments = files.map((file) => ({
                        filename: file.name,
                        mimeType: file.type,
                        file,
                      }));
                      setCampaign((prev) => ({
                        ...prev,
                        attachments: [...prev.attachments, ...newAttachments],
                      }));
                    }}
                  />
                </Box>
              ) : (
                campaign.attachments.map((attachment, index) => (
                  <Grid2 item xs={12} md={6} key={index}>
                    <Card sx={{ backgroundColor: "#fff" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "start",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6" sx={{ color: "white" }}>
                            Adjunto {index + 1}
                          </Typography>
                          <IconButton
                            onClick={() => handleRemoveAttachment(index)}
                            sx={{ color: "#ef4444" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <TextField
                          fullWidth
                          label="Nombre del archivo"
                          value={attachment.filename}
                          onChange={(e) => {
                            const newAttachments = [...campaign.attachments];
                            newAttachments[index].filename = e.target.value;
                            setCampaign((prev) => ({
                              ...prev,
                              attachments: newAttachments,
                            }));
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                              "& fieldset": { borderColor: colors.oxfordBlue },
                              "&:hover fieldset": { borderColor: colors.oxfordBlue },
                              "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                            },
                            "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                            "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                          }}
                        />

                        <TextField
                          fullWidth
                          label="Tipo MIME"
                          value={attachment.mimeType}
                          onChange={(e) => {
                            const newAttachments = [...campaign.attachments];
                            newAttachments[index].mimeType = e.target.value;
                            setCampaign((prev) => ({
                              ...prev,
                              attachments: newAttachments,
                            }));
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                              "& fieldset": { borderColor: colors.oxfordBlue },
                              "&:hover fieldset": { borderColor: colors.oxfordBlue },
                              "&.Mui-focused fieldset": { borderColor: colors.oxfordBlue },
                            },
                            "& .MuiInputLabel-root": { color: colors.oxfordBlue },
                            "& .MuiOutlinedInput-input": { color: colors.oxfordBlue },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid2>
                ))
              )}
            </Grid2>

            <Grid2
              item
              xs={12}
              md={6}
              container
              direction="column"
              style={{ minWidth: "47%", margin: "0 auto" }}
            >
              {/* Título de la campaña */}

              <iframe
                srcDoc={templates[campaign.template_html]}
                style={{ width: "100%", height: "100vh" }}
                title="Template Preview"
              />
            </Grid2>
          </Grid2>
        </TabPanel>
      </Paper>
    </Box>
  );
}

const CampaignNavbar = ({
  renderStatusBadge,
  isEditing,
  campaign,
  _id,
  saving,
  setPreviewOpen,
  handleSave,
}) => {
  // Trunca el ID a 6 caracteres finales
  const shortId = isEditing ? _id : "";

  return (
    <AppBar
      position="fixed"
      elevation={2}
      sx={{
        backgroundColor: colors.sapphire + " !important", // gray-800
        left: 210, // espacio del sidebar
        width: "calc(100% - 210px)",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          px: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT: Título + badge + ID */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 500, color: "common.white" }}
          >
            {isEditing ? "Editar Campaña" : "Nueva Campaña"}
          </Typography>

          {isEditing && (
            <>
              {/* Badge personalizado */}
              <Chip
                label={campaign.status || "—"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  bgcolor:
                    campaign.status === "Borrador"
                      ? "grey.700"
                      : "success.main",
                  color: "common.white",
                  textTransform: "capitalize",
                }}
              />

              {/* ID truncado */}
              <Typography
                variant="caption"
                noWrap
                sx={{
                  fontFamily: "monospace",
                  color: "grey.400",
                }}
              >
                {shortId}
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={!saving && <SaveOutlinedIcon fontSize="small" />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              backgroundColor: "#10b981", // emerald-500
              "&:hover": { backgroundColor: "#059669" },
              minWidth: 90,
              px: 1.5,
            }}
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
